import { AnalyticsBrowser, type EventProperties, type UserTraits } from "@segment/analytics-next";
import log from "loglevel";

import { APP_PLATFORMS, type APP_PLATFORMS_TYPE } from "./interfaces";
import { getIFrameOrigin, getIFrameOriginObject, isMain } from "./utils";

const SEGMENT_WRITE_KEY = import.meta.env.VITE_SEGMENT_WRITE_KEY;

class Analytics {
  private segment: AnalyticsBrowser;

  private globalProperties: Record<string, unknown> = {};

  public init(): void {
    if (this.isSkipped()) {
      return;
    }
    if (this.segment) {
      throw new Error("Analytics already initialized");
    }

    this.segment = new AnalyticsBrowser();
    this.segment
      .load(
        {
          writeKey: SEGMENT_WRITE_KEY,
        },
        {
          user: {
            cookie: {
              key: (isMain ? "" : getIFrameOriginObject().hostname + "_") + "ajs_user_id",
            },
            localStorage: {
              key: (isMain ? "" : getIFrameOriginObject().hostname + "_") + "ajs_user_traits",
            },
          },
        }
      )
      .then(() => {
        log.debug("Analytics initialized");
        return true;
      })
      .catch((error) => {
        log.error("Failed to initialize Analytics", error);
      });
  }

  public setGlobalProperties(properties: Record<string, unknown>) {
    this.globalProperties = { ...this.globalProperties, ...properties };
  }

  public async identify(userId: string, traits?: UserTraits) {
    if (this.isSkipped()) {
      return;
    }
    try {
      return this.getSegment().identify(userId, {
        ...traits,
      });
    } catch (error) {
      log.error(`Failed to identify user ${userId} in analytics`, error);
    }
  }

  public async track(event: string, properties?: EventProperties) {
    if (this.isSkipped()) {
      return;
    }
    try {
      return this.getSegment().track(event, {
        ...properties,
        ...this.globalProperties,
      });
    } catch (error) {
      log.error(`Failed to track event ${event}`, error);
    }
  }

  private getSegment() {
    if (!this.segment) {
      log.error("Analytics not initialized. Call Analytics.init() first.");
      throw new Error("Analytics not initialized. Call Analytics.init() first.");
    }
    return this.segment;
  }

  private isSkipped() {
    if (!SEGMENT_WRITE_KEY) {
      return true;
    }

    let dappOrigin = getIFrameOrigin();
    if (!dappOrigin) {
      // try to fetch dapp origin from dapp_name in case of non-web app
      const appPlatform = this.globalProperties.app_platform as APP_PLATFORMS_TYPE;
      if (appPlatform && appPlatform !== APP_PLATFORMS.EMBED) {
        try {
          const dappUrl = new URL((this.globalProperties.dapp_name ?? "") as string);
          dappOrigin = dappUrl.origin;
        } catch {
          dappOrigin = "";
        }
      }
    }

    // skip if the protocol is not http or https
    if (dappOrigin.startsWith("http://")) {
      return true;
    }
    // skip if dapp contains localhost
    if (dappOrigin.includes("localhost")) {
      return true;
    }

    return false;
  }
}

export const analytics = new Analytics();

analytics.setGlobalProperties({
  ws_build_env: import.meta.env.VITE_BUILD_ENV,
  ws_version: import.meta.env.VITE_BUILD_VERSION || undefined,
  dapp_url: getIFrameOrigin() || window.location.origin,
});
analytics.init();
